import React, { Component } from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import omit from 'lodash/omit';
import { MDText } from 'i18n-react';
import classNames from 'classnames';
import { EditorState } from 'draft-js';

import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';
import getPropsForComponent from '@adsk/bim360-matrix-react-helpers/es/getPropsForComponent';

import UserAvatar from '@adsk/bim360-matrix-react-user-avatar';
import MentionInput from '@adsk/bim360-matrix-react-mention-input';
import Button from '@adsk/bim360-matrix-react-button';

import './_CommentBox.scss';
import { ThemeConsumer } from '@adsk/bim360-matrix-react-helpers/es/theme';

const mentionInputCustomProps = ['className', 'style', 'placeholder', 'onChange'];

/**
 * @class CommentBox
 **/
export class CommentBox extends Component {
  /**
   * @constructor CommentBox
   **/
  constructor(props) {
    super(props);

    this.state = {
      commentText: this.props.editorState
        ? this.props.editorState.getCurrentContent().getPlainText()
        : '',
      editorState: this.props.editorState || EditorState.createEmpty(),
      isSending: false,
      isOpen: false,
      commentBoxHeight: this.props.collapsedHeight,
      commentBoxMinHeight: this.props.collapsedHeight
    };

    this._handleOnChange = this._handleOnChange.bind(this);
  }

  UNSAFE_componentWillUpdate(nextProps, nextState) {
    if (this.state.isOpen !== nextState.isOpen) {
      this.toggleCommentBox(this.state.isOpen);
    }
  }

  toggleCommentBox(isCollapsing) {
    this.setState({
      commentBoxHeight: isCollapsing ? this.props.collapsedHeight : this.props.expandedHeight,
      commentBoxMinHeight: isCollapsing ? this.props.collapsedHeight : this.props.expandedHeight
    });
  }

  _handleOnSend() {
    this.setState({ isSending: true }, () => {
      this.props
        .onSend(this.state.commentText.trim())
        .then(() => {
          this.mentionInputRef.clearEditor();
          this.setState({ commentText: '', isSending: false, isOpen: false });
        })
        .catch(() => {
          this.props.onSendFailure();
          this.setState({ isSending: false });
        });
    });
  }

  _handleOnFocus() {
    this.setState({ isOpen: true });
    this.props.onFocus(this.state.commentText);
  }

  _handleOnBlur() {
    this.setState({ isOpen: !!this.state.commentText });
    this.props.onBlur(this.state.commentText);
  }

  isEmpty(commentText) {
    const textOnly = commentText.replace(/(\s|\r)/gm, '');
    return textOnly.length === 0;
  }

  _handleOnChange(text, editorState) {
    const newState = { commentText: text };
    // MentionInput triggers onChange on mount so we need to guard against it here
    if (this.state.isOpen) {
      const commentBoxHeight = text.length > 0 ? 'auto' : this.props.expandedHeight;
      newState.commentBoxHeight = commentBoxHeight;
    }

    this.setState(newState);
    this.props.onChange(text, editorState);
  }

  /**
   * Render
   * @returns {VDOM}
   **/
  render() {
    const sendTranslationKey = this.state.isSending ? 'COMMENT_BOX_SENDING' : 'COMMENT_BOX_SEND';
    const placeholderText = this.props.disableMention
      ? 'COMMENT_BOX_PLACEHOLDER'
      : 'COMMENT_BOX_PLACEHOLDER_WITH_MENTION';
    const inputClasses = classNames('CommentBox__input', this.props.inputClassName, {
      'CommentBox__input--is-open': this.state.isOpen
    });

    return (
      <ThemeConsumer>
        {({ theme }) => (
          <div
            className={classNames('CommentBox', this.props.className, `CommentBox--${theme}`)}
            style={{
              height: this.state.commentBoxHeight,
              minHeight: this.state.commentBoxMinHeight
            }}
          >
            <UserAvatar user={this.props.currentUser} />
            <MentionInput
              ref={ref => (this.mentionInputRef = ref)}
              {...omit(getPropsForComponent(this.props, MentionInput), mentionInputCustomProps)}
              onFocus={() => this._handleOnFocus()}
              onBlur={() => this._handleOnBlur()}
              onChange={this._handleOnChange}
              editorState={this.props.editorState}
              placeholder={this.props[TSymbolProp].translate(placeholderText)}
              className={inputClasses}
              disableMention={this.props.disableMention}
            />
            {this.state.isOpen && (
              <Button
                styleType={Button.StyleType.PRIMARY}
                className="CommentBox__send"
                onClick={() => this._handleOnSend()}
                disabled={this.isEmpty(this.state.commentText) || this.state.isSending}
              >
                {this.props[TSymbolProp].translate(sendTranslationKey)}
              </Button>
            )}
          </div>
        )}
      </ThemeConsumer>
    );
  }
}

CommentBox.defaultProps = {
  onSend: noop,
  onSendFailure: noop,
  expandedHeight: '110px',
  collapsedHeight: '70px',
  onFocus: noop,
  onBlur: noop,
  onChange: noop,
  disableMention: false
};

CommentBox.propTypes = {
  /**
   * EditorState for the rendered MentionInput. See MentionInput's props documentation for further info.
   */
  editorState: PropTypes.object,
  /**
   * The list of suggestions to display when triggering a mention.
   * This array should have a structure that the `menuAdapter` knows how to handle.
   */
  suggestions: PropTypes.array.isRequired,
  /**
   * Selector function to get the name to display in the suggestion.
   */
  getSuggestionName: PropTypes.func,
  /**
   * Selector function to get the id to be as the key when rendering the suggestion list.
   */
  getSuggestionId: PropTypes.func,
  /**
   * Selector function to get the name when generating the mention text.
   */
  getMentionName: PropTypes.func,
  /**
   * Selector function to get the id when generating the mention text.
   */
  getMentionId: PropTypes.func,
  /**
   * Formatter function that generates the mention text according to the mention and mention selector functions above.
   */
  mentionFormatter: PropTypes.func,
  /**
   * The menu adapter component.
   * The component **MUST** have a static function called `buildSuggestions` that takes the suggestions and the suggestions selector functions above.
   * The component **MAY** have a static property called `theme` that defines custom className for the internal mention objects.
   * See "Configuration Parameters" section in https://www.draft-js-plugins.com/plugin/mention for more information.
   */
  menuAdapter: PropTypes.func,
  /**
   * A component used for displaying a single entry in the suggestion list.
   */
  entryComponent: PropTypes.func,
  /**
   * Called when the input is being changed (the comment text is being passed to this callback)
   */
  onChange: PropTypes.func,
  /**
   * Called when the input is being focused (the comment text is being passed to this callback)
   */
  onFocus: PropTypes.func,
  /**
   * Called when the input is being blurred (the comment text is being passed to this callback)
   */
  onBlur: PropTypes.func,
  /**
   * Called when a 'keyup' event has been called on the input.
   */
  onKeyUp: PropTypes.func,
  /**
   * The current user who's doing the comment
   */
  currentUser: UserAvatar.propTypes.user,
  /**
   * A promise that's called when clicking the send button
   */
  onSend: PropTypes.func,
  /**
   * A callback to run in case onSend results in a failure
   */
  onSendFailure: PropTypes.func,
  /**
   * The locale of the component
   */
  locale: PropTypes.string,
  /**
   * Optional class name to give to the component
   */
  className: PropTypes.string,
  /**
   * Optional class name to give to the input
   */
  inputClassName: PropTypes.string,
  /**
   * The collapsed height of the comment box (defaults to 70px)
   */
  collapsedHeight: PropTypes.string,
  /**
   * The height to expand to when comment box is focused (defaults to 110px)
   */
  expandedHeight: PropTypes.string,
  /**
   * Whether to disable the display of the Mention.
   */
  disableMention: PropTypes.bool
};

export default withLocale(CommentBox, ({ locale }) => new MDText(require(`./i18n/${locale}.json`)));
